import React, { Component } from 'react';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';

import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { createResourceLocatorString } from '../../../util/routes';
import { getSearchPageResourceLocatorStringParams } from '../SearchPage.shared';

import { ModalInMobile, Button } from '../../../components';
import {
  MOBILE_BOTTOM_NAV_SUPPRESS_MS,
  setMobileBottomNavSuppressed,
} from '../../TopbarContainer/Topbar/MobileBottomNav/mobileBottomNavHelpers';

import PopupOpenerButton from '../PopupOpenerButton/PopupOpenerButton';
import css from './SearchFiltersMobile.module.css';

// Match ModalInMobile bottomSheetSlideUp duration so open/close feel symmetric.
const SHEET_CLOSE_MS = MOBILE_BOTTOM_NAV_SUPPRESS_MS;

class SearchFiltersMobileComponent extends Component {
  constructor(props) {
    super(props);
    this.state = { isFiltersOpenOnMobile: false, initialQueryParams: null };

    this.modalSheetRef = React.createRef();
    this.filtersScrollRef = React.createRef();
    this.headerRef = React.createRef();
    this.tagsRef = React.createRef();
    this.dragState = { startY: 0, startScrollTop: 0, dragging: false, offset: 0 };
    this.isClosingSheet = false;
    this.closeAnimTimeout = null;

    this.openFilters = this.openFilters.bind(this);
    this.cancelFilters = this.cancelFilters.bind(this);
    this.requestCloseFilters = this.requestCloseFilters.bind(this);
    this.closeFilters = this.closeFilters.bind(this);
    this.resetAll = this.resetAll.bind(this);
    this.handleSheetTouchStart = this.handleSheetTouchStart.bind(this);
    this.handleSheetTouchMove = this.handleSheetTouchMove.bind(this);
    this.handleSheetTouchEnd = this.handleSheetTouchEnd.bind(this);
    this.attachSheetTouchListeners = this.attachSheetTouchListeners.bind(this);
    this.detachSheetTouchListeners = this.detachSheetTouchListeners.bind(this);
    this.animateSheetClosed = this.animateSheetClosed.bind(this);
    this.clearCloseAnimation = this.clearCloseAnimation.bind(this);
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.isFiltersOpenOnMobile && !prevState.isFiltersOpenOnMobile) {
      // Clear any leftover inline styles from a prior dismiss so the CSS slide-up can run.
      this.clearSheetInlineStyles();
      this.attachSheetTouchListeners();
    } else if (!this.state.isFiltersOpenOnMobile && prevState.isFiltersOpenOnMobile) {
      this.detachSheetTouchListeners();
      this.clearSheetInlineStyles();
    }
  }

  componentWillUnmount() {
    this.clearCloseAnimation();
    this.detachSheetTouchListeners();
    setMobileBottomNavSuppressed(false);
  }

  clearCloseAnimation() {
    if (this.closeAnimTimeout) {
      clearTimeout(this.closeAnimTimeout);
      this.closeAnimTimeout = null;
    }
    this.isClosingSheet = false;
  }

  attachSheetTouchListeners() {
    const sheet = this.modalSheetRef.current;
    if (!sheet || sheet.dataset.touchBound) {
      return;
    }
    sheet.addEventListener('touchstart', this.handleSheetTouchStart, { passive: true });
    sheet.addEventListener('touchmove', this.handleSheetTouchMove, { passive: false });
    sheet.addEventListener('touchend', this.handleSheetTouchEnd, { passive: true });
    sheet.addEventListener('touchcancel', this.handleSheetTouchEnd, { passive: true });
    sheet.dataset.touchBound = 'true';
  }

  detachSheetTouchListeners() {
    const sheet = this.modalSheetRef.current;
    if (!sheet || !sheet.dataset.touchBound) {
      return;
    }
    sheet.removeEventListener('touchstart', this.handleSheetTouchStart);
    sheet.removeEventListener('touchmove', this.handleSheetTouchMove);
    sheet.removeEventListener('touchend', this.handleSheetTouchEnd);
    sheet.removeEventListener('touchcancel', this.handleSheetTouchEnd);
    delete sheet.dataset.touchBound;
  }

  isDragFromHeader(target) {
    const header = this.headerRef.current;
    const tags = this.tagsRef.current;
    return (header && header.contains(target)) || (tags && tags.contains(target));
  }

  handleSheetTouchStart(event) {
    if (this.isClosingSheet) {
      return;
    }
    const scrollEl = this.filtersScrollRef.current;
    const touch = event.touches[0];
    this.dragState = {
      startY: touch.clientY,
      startScrollTop: scrollEl ? scrollEl.scrollTop : 0,
      dragging: false,
      offset: 0,
      fromHeader: this.isDragFromHeader(event.target),
    };
  }

  handleSheetTouchMove(event) {
    if (this.isClosingSheet) {
      return;
    }
    const { startY, startScrollTop, dragging, fromHeader } = this.dragState;
    const touch = event.touches[0];
    const deltaY = touch.clientY - startY;
    const canDrag = fromHeader || startScrollTop <= 0;

    if (deltaY > 0 && canDrag) {
      if (!dragging && deltaY > 8) {
        this.dragState.dragging = true;
      }
      if (this.dragState.dragging) {
        event.preventDefault();
        const offset = Math.max(0, deltaY);
        this.dragState.offset = offset;
        this.setSheetTransform(offset);
      }
    }
  }

  handleSheetTouchEnd() {
    if (this.isClosingSheet) {
      return;
    }
    const { dragging, offset } = this.dragState;
    this.dragState = {
      startY: 0,
      startScrollTop: 0,
      dragging: false,
      offset: 0,
      fromHeader: false,
    };
    if (dragging && offset > 80) {
      this.animateSheetClosed(offset, () => this.closeFilters());
    } else if (dragging) {
      this.resetSheetTransform(true);
    }
  }

  // Prefer the bottom-sheet container (same node as the open slide-up) so drag/close
  // move the full chrome; fall back to the inner sheet if the DOM shape changes.
  getSheetEl() {
    const sheet = this.modalSheetRef.current;
    if (!sheet) {
      return null;
    }
    const content = sheet.parentElement;
    const container = content && content.parentElement;
    return container || sheet;
  }

  clearSheetInlineStyles() {
    const sheet = this.getSheetEl();
    if (sheet) {
      sheet.style.animation = '';
      sheet.style.transition = '';
      sheet.style.transform = '';
    }
  }

  setSheetTransform(offset) {
    const sheet = this.getSheetEl();
    if (sheet) {
      // Disable the open keyframes so they don't fight the drag transform.
      sheet.style.animation = 'none';
      sheet.style.transform = `translateY(${offset}px)`;
      sheet.style.transition = 'none';
    }
  }

  resetSheetTransform(animate = false) {
    const sheet = this.getSheetEl();
    if (sheet) {
      // Keep animation disabled during snap-back so the open keyframes don't re-fire.
      sheet.style.animation = 'none';
      sheet.style.transition = animate ? 'transform 0.2s ease-out' : '';
      sheet.style.transform = '';
    }
  }

  /**
   * Continue the drag gesture by sliding the sheet off-screen, then run onDone.
   * Closing immediately after a drag feels like a hard cut because open uses a slide-up.
   */
  animateSheetClosed(startOffset, onDone) {
    const sheet = this.getSheetEl();
    if (!sheet) {
      setMobileBottomNavSuppressed(false);
      onDone();
      return;
    }

    this.isClosingSheet = true;
    // Keep nav suppressed until onDone (cancelFilters / closeFilters). Restoring mid-dismiss
    // lets the fixed nav (z-index 15) paint above the sheet, which is trapped under
    // SearchPage's layoutWrapperContainer (z-index: topbar - 1).

    // Pin the current drag position, then animate the rest of the way down.
    sheet.style.animation = 'none';
    sheet.style.transition = 'none';
    sheet.style.transform = `translateY(${startOffset}px)`;
    // Force reflow so the browser applies the starting transform before transitioning.
    void sheet.offsetHeight;

    const finish = () => {
      if (!this.isClosingSheet) {
        return;
      }
      sheet.removeEventListener('transitionend', onTransitionEnd);
      this.clearCloseAnimation();
      onDone();
    };

    const onTransitionEnd = event => {
      if (event.target !== sheet || event.propertyName !== 'transform') {
        return;
      }
      finish();
    };

    sheet.addEventListener('transitionend', onTransitionEnd);
    sheet.style.transition = `transform ${SHEET_CLOSE_MS / 1000}s ease-out`;
    sheet.style.transform = 'translateY(100%)';

    this.closeAnimTimeout = setTimeout(finish, SHEET_CLOSE_MS + 50);
  }

  // Open filters modal, set the initial parameters to current ones
  openFilters() {
    const { onOpenModal, urlQueryParams } = this.props;
    onOpenModal();
    // Fade nav out with the sheet slide-up (same 280ms timing).
    setMobileBottomNavSuppressed(true);
    this.setState({ isFiltersOpenOnMobile: true, initialQueryParams: urlQueryParams }, () => {
      this.attachSheetTouchListeners();
    });
  }

  // Animate closed (X / Escape), then revert filter params via cancelFilters.
  requestCloseFilters() {
    if (this.isClosingSheet || !this.state.isFiltersOpenOnMobile) {
      return;
    }

    // Modal.handleClose unlocks body scroll before onClose runs. Re-lock for the
    // slide-down window so the page cannot move under the still-visible sheet.
    const { onManageDisableScrolling } = this.props;
    if (onManageDisableScrolling) {
      onManageDisableScrolling('SearchFiltersMobile.filters', true);
    }

    // Continue from the current drag offset so Escape/X mid-drag does not jump up.
    const startOffset = this.dragState.offset || 0;
    this.dragState = {
      startY: 0,
      startScrollTop: 0,
      dragging: false,
      offset: 0,
      fromHeader: false,
    };
    this.animateSheetClosed(startOffset, () => this.cancelFilters());
  }

  // Close the filters by clicking cancel, revert to the initial params
  cancelFilters() {
    const { history, onCloseModal, location, routeConfiguration } = this.props;

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(
      createResourceLocatorString(
        routeName,
        routeConfiguration,
        pathParams,
        this.state.initialQueryParams
      )
    );
    // Restore after slide-down so the nav fades in once the sheet is gone.
    setMobileBottomNavSuppressed(false);
    onCloseModal();
    this.setState({ isFiltersOpenOnMobile: false, initialQueryParams: null });
  }

  // Apply current filters and close immediately (no slide-down — intentional).
  closeFilters() {
    // Transform cleanup happens in componentDidUpdate. Resetting here after a
    // drag-dismiss animation would flash the sheet back on-screen for a frame.
    // Soft-restore nav (CSS fade) as the sheet closes.
    setMobileBottomNavSuppressed(false);
    this.props.onCloseModal();
    this.setState({ isFiltersOpenOnMobile: false, initialQueryParams: null });
  }

  // Reset all filter query parameters
  resetAll(e) {
    this.props.resetAll(e);

    // blur event target if event is passed
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  }

  render() {
    const {
      rootClassName,
      className,
      children,
      sortByComponent,
      listingsAreLoaded,
      resultsCount,
      searchInProgress = false,
      showAsModalMaxWidth,
      onMapIconClick = () => {},
      onMapViewToggle = () => {},
      onManageDisableScrolling,
      selectedFiltersCount = 0,
      activeFilterTags = null,
      noResultsInfo,
      intl,
      isMapVariant = true,
      showMapViewToggle = false,
      isMapViewActive = false,
    } = this.props;

    const classes = classNames(
      rootClassName || css.root,
      className,
      this.state.isFiltersOpenOnMobile && css.rootModalOpen
    );

    const resultsFound = (
      <FormattedMessage id="SearchFiltersMobile.foundResults" values={{ count: resultsCount }} />
    );
    const noResults = <FormattedMessage id="SearchFiltersMobile.noResults" />;
    const loadingResults = <FormattedMessage id="SearchFiltersMobile.loadingResults" />;
    const filtersHeading = intl.formatMessage({ id: 'SearchFiltersMobile.heading' });
    const modalCloseButtonMessage = intl.formatMessage({ id: 'SearchFiltersMobile.close' });
    const clearAllLabel = intl.formatMessage({ id: 'SearchFiltersMobile.clearAll' });

    const showListingsLabel = intl.formatMessage(
      { id: 'SearchFiltersMobile.showListings' },
      { count: resultsCount }
    );

    return (
      <div className={classes}>
        <div className={css.mobileToolbar}>
          <div className={css.searchResultSummary}>
            {listingsAreLoaded && resultsCount > 0 ? resultsFound : null}
            {listingsAreLoaded && resultsCount === 0 ? noResults : null}
            {searchInProgress ? loadingResults : null}
          </div>
          <div className={css.buttons}>
            <PopupOpenerButton isSelected={selectedFiltersCount > 0} toggleOpen={this.openFilters}>
              <FormattedMessage
                id="SearchFiltersMobile.filtersButtonLabel"
                className={css.mapIconText}
              />
            </PopupOpenerButton>

            {sortByComponent}
            {showMapViewToggle ? (
              <button
                type="button"
                className={isMapViewActive ? css.mapViewToggleActive : css.mapViewToggle}
                onClick={() => onMapViewToggle(!isMapViewActive)}
                aria-pressed={isMapViewActive}
              >
                <FormattedMessage
                  id={isMapViewActive ? 'SearchPage.viewAsList' : 'SearchPage.viewAsMap'}
                />
              </button>
            ) : isMapVariant ? (
              <div className={css.mapIcon} onClick={onMapIconClick}>
                <FormattedMessage
                  id="SearchFiltersMobile.openMapView"
                  className={css.mapIconText}
                />
              </div>
            ) : null}
          </div>
        </div>

        {noResultsInfo ? noResultsInfo : null}

        <ModalInMobile
          id="SearchFiltersMobile.filters"
          isModalOpenOnMobile={this.state.isFiltersOpenOnMobile}
          onClose={this.requestCloseFilters}
          showAsModalMaxWidth={showAsModalMaxWidth}
          onManageDisableScrolling={onManageDisableScrolling}
          containerClassName={css.modalContainer}
          closeButtonMessage={modalCloseButtonMessage}
          presentation="bottomSheet"
        >
          <div className={css.modalSheet} ref={this.modalSheetRef}>
            <div className={css.modalHeadingWrapper} ref={this.headerRef}>
              <div className={css.dragHandle} aria-hidden="true" />
              <span className={css.modalHeading}>{filtersHeading}</span>
            </div>
            {this.state.isFiltersOpenOnMobile && activeFilterTags ? (
              <div className={css.activeFilterTags} ref={this.tagsRef}>
                {activeFilterTags}
              </div>
            ) : null}
            {this.state.isFiltersOpenOnMobile ? (
              <div className={css.filtersWrapper} ref={this.filtersScrollRef}>
                {children}
              </div>
            ) : null}

            <div className={css.showListingsContainer}>
              <button type="button" className={css.clearAllButton} onClick={e => this.resetAll(e)}>
                {clearAllLabel}
              </button>
              <Button className={css.showListingsButton} onClick={this.closeFilters}>
                {showListingsLabel}
              </Button>
            </div>
          </div>
        </ModalInMobile>
      </div>
    );
  }
}

/**
 * SearchFiltersMobile component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {Object} props.urlQueryParams - The URL query params
 * @param {React.Node} props.sortByComponent - The sort by component
 * @param {boolean} props.listingsAreLoaded - Whether the listings are loaded
 * @param {number} props.resultsCount - The number of results
 * @param {boolean} props.searchInProgress - Whether the search is in progress
 * @param {number} props.showAsModalMaxWidth - The maximum width of the modal
 * @param {Function} props.onMapIconClick - The function to click the map icon
 * @param {Function} props.onManageDisableScrolling - The function to manage disable scrolling
 * @param {Function} props.onOpenModal - The function to open the modal
 * @param {Function} props.onCloseModal - The function to close the modal
 * @param {Function} props.resetAll - The function to reset all
 * @param {number} props.selectedFiltersCount - The number of selected filters
 * @param {React.Node} [props.activeFilterTags] - Active filter tags component
 * @param {boolean} props.isMapVariant - Whether the map variant is enabled
 * @param {boolean} [props.showMapViewToggle] - Show List/Map toggle (grid search with map view)
 * @param {boolean} [props.isMapViewActive] - Whether map view is active (with showMapViewToggle)
 * @param {Function} [props.onMapViewToggle] - Toggle map view (with showMapViewToggle)
 * @returns {JSX.Element}
 */
const SearchFiltersMobile = props => {
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();

  return (
    <SearchFiltersMobileComponent
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      {...props}
    />
  );
};

export default SearchFiltersMobile;
